import { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Paper, TextField, Typography } from '@mui/material';
import { PROMO_SURFACES, parseAppVersion } from './promoSurfacesUtils';

function slotLines(slot) {
  if (!slot) return ['Hidden'];
  const lines = [];
  if (slot.label) lines.push(slot.label);
  if (slot.link) lines.push(slot.link);
  if (slot.media?.kind === 'video') lines.push(slot.media.m3u8_url || 'Video, no playlist yet');
  if (slot.media?.kind === 'lottie_remote') lines.push(slot.media.uri || 'Lottie, no URL yet');
  if (!lines.length) lines.push('Shown');
  return lines;
}

function LivePreviewPanel({ preview, loading, error, appVersion = '', onApplyVersion }) {
  const [draft, setDraft] = useState(appVersion);
  const [versionError, setVersionError] = useState('');

  useEffect(() => {
    setDraft(appVersion);
  }, [appVersion]);

  const apply = () => {
    const parsed = parseAppVersion(draft);
    if (!parsed.empty && !parsed.version) {
      setVersionError('App version must be a version like 2.3.0.');
      return;
    }
    setVersionError('');
    onApplyVersion(parsed.version || '');
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
        What the app receives
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        A slot appears here only when its surface is on and the active template is eligible. Leave the version empty to
        include slots that have version bounds. Enter a version to apply the same inclusive filter as the app. A slot
        outside that range is hidden, and the campaign id is rebuilt from the slots that remain. Changing which
        template is active also changes the campaign id. The app resets its day counters when that happens.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap', mb: 1.5 }}>
        <TextField
          size="small"
          label="App version"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') apply();
          }}
          placeholder="2.7.0"
          helperText={versionError || 'Optional. Same inclusive filter as the app.'}
          error={Boolean(versionError)}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="outlined" onClick={apply} sx={{ mt: 0.5 }} disabled={loading}>
          Preview
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {loading && !preview ? (
        <Typography variant="body2">Loading the live preview…</Typography>
      ) : (
        <>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Campaign:{' '}
            <Box component="span" sx={{ fontFamily: 'ui-monospace, monospace' }}>
              {preview?.campaign_id || 'none — every slot is hidden'}
            </Box>
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
            {PROMO_SURFACES.map((surface) => {
              const slot = preview?.[surface.key];
              const lines = slotLines(slot);
              return (
                <Box key={surface.key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {surface.title}
                    </Typography>
                    <Chip size="small" label={slot ? 'Shown' : 'Hidden'} color={slot ? 'success' : 'default'} />
                  </Box>
                  {lines.map((line) => (
                    <Typography key={line} variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                      {line}
                    </Typography>
                  ))}
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Paper>
  );
}

export default LivePreviewPanel;
